import { useState } from "react";

import { retiredToolNames, type McpCatalog, type McpQuotaPatch } from "../../api/mcp";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useWrite } from "../../hooks/useWrite";
import { Gate } from "../authority/Gate";
import { Notice } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../write/EffectTiming";
import { WriteFailure } from "../write/WriteFailure";

/**
 * Caps whose tool MCP no longer registers.
 *
 * `PATCH /admin/quota/{tier}` validates the whole merged document, so one
 * stale name turns every later edit of the tier into a 422. Nulls are
 * removed before that check, which is why every stale name goes in one body.
 */
export function RetiredCaps({
  config,
  tier,
  caps,
  catalog,
  effectiveWithinSeconds,
  onDone,
}: {
  config: AppConfig;
  tier: string;
  caps: Record<string, number>;
  catalog: McpCatalog | null;
  effectiveWithinSeconds?: number;
  onDone: () => void;
}) {
  const names = retiredToolNames(caps, catalog);
  const write = useWrite<McpQuotaPatch>(config);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<McpQuotaPatch | null>(null);
  const busy = write.state.kind === "submitting";

  if (names.length === 0 && !result && write.state.kind === "idle") return null;

  function submit(reason: string) {
    const body: Record<string, unknown> = {
      tools: Object.fromEntries(names.map((name) => [name, null])),
    };
    if (reason) body.reason = reason;
    void write
      .submit(`/mcp/admin/quota/${encodeURIComponent(tier)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        onDone();
      });
    setOpen(false);
  }

  return (
    <>
      {names.length > 0 && (
        <Notice tone="amber" title="Retired-tool caps">
          <p>
            These caps name tools that are not in the catalog. A stored cap on a retired tool
            blocks every later edit of this tier.
          </p>
          <ul className="mt-2">
            {names.map((name) => (
              <li key={name}>
                <span className="ref">{name}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <Gate need="quota:write">
              <Button disabled={busy} onClick={() => setOpen(true)}>
                Clear retired caps
              </Button>
            </Gate>
          </div>
        </Notice>
      )}
      {result && (
        <div className="mt-3">
          <WriteResult title="Retired caps cleared" onDismiss={() => setResult(null)}>
            <p className="text-sm">{effectFromSeconds(result.effective_within_seconds, "This")}</p>
          </WriteResult>
        </div>
      )}
      <WriteFailure state={write.state} onDismiss={write.reset} onReread={onDone} />
      <ConfirmDialog
        open={open}
        title={`Clear retired caps on ${tier}`}
        confirmLabel="Clear them"
        requireReason
        reasonHint="why these caps, e.g. DA-9"
        busy={busy}
        onConfirm={submit}
        onCancel={() => setOpen(false)}
        effect={effectFromSeconds(effectiveWithinSeconds, "This")}
      >
        <p>
          One change removes {names.map((name) => name).join(", ")} from{" "}
          <span className="ref">{tier}</span>. Each name is sent as null, so the check that
          rejects an unknown tool never sees them.
        </p>
      </ConfirmDialog>
    </>
  );
}
